/**
 * QultureUp Hooks
 *
 * React Query hooks for QultureUp learning worlds, tracks, and missions.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QultureUpPartnerWorld } from '@tonnie/types';
import { useSdkContext } from '../providers/SdkProvider';
import { useAuth } from '../providers/AuthProvider';
import { heroesKeys } from './useHeroes';
import { languageAcademyKeys } from './useLanguageAcademy';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UnlockCondition {
  type: 'none' | 'world_completed' | 'track_completed' | 'assessment_level';
  value: string | null;
}

/**
 * Why an item is locked (server-authoritative gating). `billing` = the journey
 * is on the free/basic tier and the item is outside the freemium slice (show an
 * upgrade CTA); `progression` = an earlier world/track must be finished first;
 * `level` = above the user's assessed level; `null` = unlocked.
 */
export type LockReason = 'billing' | 'progression' | 'level' | null;

/** A Tonnie content world: the destination's tracks and missions, gated, with progress. */
export interface World {
  kind: 'content';
  id: string;
  title: string;
  /** @deprecated Use `title` */
  name?: string;
  description: string;
  sortOrder: number;
  iconUrl: string;
  /** @deprecated Use `iconUrl` */
  imageUrl?: string;
  icon?: string;
  unlockCondition: UnlockCondition;
  tracksTotal: number;
  tracksCompleted: number;
  missionsTotal: number;
  missionsCompleted: number;
  progress: number;
  /**
   * The server's verdict: every mission of the world completed or placed out
   * of by the level test, at least one completed. A world can be complete
   * below 100% `progress` — draw this, never derive it.
   */
  isCompleted: boolean;
  isUnlocked: boolean;
  /** @deprecated Use `isUnlocked` */
  isLocked?: boolean;
  /** Why this world is locked (billing / progression); null when unlocked. */
  lockReason?: LockReason;
  tracks?: Track[];
}

/**
 * A World that opens a partner's product (the Languages Academy) — no tracks,
 * missions or progress; never counted. The screen names it from its `code`.
 */
export type PartnerWorld = QultureUpPartnerWorld;

/**
 * One entry of the worlds list (`GET /heroes/qultureup/worlds`): the content
 * worlds, then the partner worlds the citizen sees on the journey. Narrow on
 * `kind` — every count reads the content worlds only (`contentWorldsOf`).
 */
export type WorldListItem = World | PartnerWorld;

/** The content worlds of a worlds list — what every tally, tab and banner reads. */
export function contentWorldsOf(items: readonly WorldListItem[]): World[] {
  const worlds: World[] = [];
  for (const item of items) {
    switch (item.kind) {
      case 'content':
        worlds.push(item);
        break;
      case 'partner':
        break;
      default: {
        const unknownKind: never = item;
        throw new Error(`contentWorldsOf: unhandled world kind ${JSON.stringify(unknownKind)}`);
      }
    }
  }
  return worlds;
}

/** The partner worlds of a worlds list, in the server's order (after the content worlds). */
export function partnerWorldsOf(items: readonly WorldListItem[]): PartnerWorld[] {
  const worlds: PartnerWorld[] = [];
  for (const item of items) {
    switch (item.kind) {
      case 'content':
        break;
      case 'partner':
        worlds.push(item);
        break;
      default: {
        const unknownKind: never = item;
        throw new Error(`partnerWorldsOf: unhandled world kind ${JSON.stringify(unknownKind)}`);
      }
    }
  }
  return worlds;
}

export interface Track {
  id: string;
  worldId: string;
  title: string;
  /** @deprecated Use `title` */
  name?: string;
  description: string;
  sortOrder: number;
  estimatedTimeMinutes: number;
  iconUrl: string;
  unlockCondition: UnlockCondition;
  missionCount?: number;
  missionsTotal?: number;
  missionsCompleted?: number;
  progress: number;
  isUnlocked: boolean;
  /** @deprecated Use `isUnlocked` */
  isLocked?: boolean;
  /** Why this track is locked (billing / progression); null when unlocked. */
  lockReason?: LockReason;
  level?: string;
  status?: 'not_started' | 'in_progress' | 'completed' | 'skipped' | 'locked';
  missions?: Mission[];
}

export interface ContentSection {
  id: number;
  title: string;
  content: string;
  videoUrl: string | null;
}

export interface QuizAnswer {
  id: number;
  content: string;
  imageUrl?: string;
}

export interface QuizQuestion {
  id: number;
  title: string;
  question: string | null;
  type: 'SINGLE_CHOICE' | 'SINGLE_IMAGE_CHOICE' | 'OPEN_ANSWER';
  possibleAnswers: QuizAnswer[] | null;
}

export interface Mission {
  id: string;
  trackId: string;
  title: string;
  description: string;
  estimatedTimeMinutes: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  milesReward: { base: number; max: number };
  sortOrder: number;
  /**
   * Public S3 URL for the mission's hero artwork. Generated per-mission and
   * served from the QultureUp assets bucket
   * (`qultureup/missions/<world>/<track>/<slug>.png`). Optional for legacy
   * catalog entries that may predate the artwork pipeline.
   */
  iconUrl?: string;
  /**
   * Optional YouTube video rendered at the top of the mobile Mission Screen
   * (above the mission text), played inline with a fullscreen option. A full
   * watch/`youtu.be`/`embed` URL; optional — most missions have none.
   */
  youtubeVideoUrl?: string | null;
  contentSections: ContentSection[];
  quiz: QuizQuestion[];
  status: 'not_started' | 'in_progress' | 'completed' | 'skipped' | 'locked';
  /** Why this mission is `locked` (billing / progression / level); null otherwise. */
  lockReason?: LockReason;
  score: number | null;
  attempts: number;
  completedAt: string | null;
  /** True when assessment level allows this mission to be skipped */
  isSkippable?: boolean;
}

export interface QultureUpProgress {
  totalMilesEarned: number;
  missionsCompleted: number;
  worldsCompleted: number;
  tracksCompleted: number;
  currentStreak: number;
  longestStreak: number;
  weeklyProgress: number[];
  totalTimeSpentMinutes: number;
  worldProgress: Record<
    string,
    { missionsCompleted: number; missionsTotal: number; progress: number }
  >;
}

export interface QultureUpAssessment {
  id: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  levelScore: number;
  completedAt: string | null;
  skipWorldIds: string[];
  skipTrackIds: string[];
  assessmentData: Record<string, unknown> | null;
  isActive: boolean;
}

export interface QuizEvaluationResult {
  missionId: string;
  totalQuestions: number;
  correctAnswers: number;
  scorePercent: number;
  passed: boolean;
  milesEarned: number;
  bonusMiles?: number;
  results: Array<{
    questionId: number;
    isCorrect: boolean;
    pointsEarned: number;
    feedback?: string;
  }>;
}

// ─── Query Keys ───────────────────────────────────────────────────────────────

// ─── Assessment Question Types ─────────────────────────────────────────────────

export interface AssessmentQuestion {
  questionId: number;
  missionId: string;
  trackId: string;
  worldId: string;
  title: string;
  question: string | null;
  type: 'SINGLE_CHOICE' | 'SINGLE_IMAGE_CHOICE' | 'OPEN_ANSWER';
  possibleAnswers: Array<{ id: number; content: string; imageUrl?: string }> | null;
  difficultyTier: 'easy' | 'medium' | 'hard';
}

/**
 * A knowledge-assessment attempt the server started: the questions it picked
 * and recorded, with the id every answer and the placement are read against.
 */
export interface AssessmentAttempt {
  attemptId: string;
  worldId: string;
  worldTitle: string;
  questions: AssessmentQuestion[];
  totalQuestionsInPool: number;
  questionsPerWorld: number;
}

/** The placement the server derives from an attempt's recorded answers. */
export interface AssessmentPlacement {
  level: 'beginner' | 'intermediate' | 'advanced';
  levelScore: number;
  levelPerWorld: Record<
    string,
    {
      level: 'beginner' | 'intermediate' | 'advanced';
      score: number;
      correct: number;
      total: number;
      skipMissionIds: string[];
    }
  >;
}

export interface AssessmentEvaluationResult {
  isCorrect: boolean;
  feedback?: string;
  correctAnswer?: string;
}

export const qultureUpKeys = {
  all: ['qultureup'] as const,
  worlds: (journeyId?: string) => [...qultureUpKeys.all, 'worlds', journeyId ?? 'default'] as const,
  world: (id: string, journeyId?: string) =>
    [...qultureUpKeys.all, 'world', id, journeyId ?? 'default'] as const,
  tracks: (worldId: string, journeyId?: string) =>
    [...qultureUpKeys.all, 'tracks', worldId, journeyId ?? 'default'] as const,
  missions: (worldId: string, trackId: string, journeyId?: string) =>
    [...qultureUpKeys.all, 'missions', worldId, trackId, journeyId ?? 'default'] as const,
  mission: (id: string, journeyId?: string) =>
    [...qultureUpKeys.all, 'mission', id, journeyId ?? 'default'] as const,
  progress: (journeyId?: string) =>
    [...qultureUpKeys.all, 'progress', journeyId ?? 'default'] as const,
  assessment: (journeyId?: string) =>
    [...qultureUpKeys.all, 'assessment', journeyId ?? 'default'] as const,
  assessmentPlacement: (attemptId: string, journeyId?: string) =>
    [...qultureUpKeys.all, 'assessment-placement', attemptId, journeyId ?? 'default'] as const,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function journeyParam(journeyId?: string): string {
  return journeyId ? `?journeyId=${journeyId}` : '';
}

function appendJourneyParam(url: string, journeyId?: string): string {
  if (!journeyId) return url;
  return url.includes('?') ? `${url}&journeyId=${journeyId}` : `${url}?journeyId=${journeyId}`;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * The journey's worlds list: the content worlds with progress, then the
 * partner worlds the citizen sees (`WorldListItem` — narrow on `kind`).
 */
export function useWorlds(journeyId?: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();

  return useQuery({
    queryKey: qultureUpKeys.worlds(journeyId),
    queryFn: async () => {
      const response = await client.get<WorldListItem[]>(
        `/api/heroes/qultureup/worlds${journeyParam(journeyId)}`
      );
      return response;
    },
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get world by ID with tracks
 */
export function useWorld(id: string, journeyId?: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();

  return useQuery({
    queryKey: qultureUpKeys.world(id, journeyId),
    queryFn: async () => {
      const response = await client.get<World & { tracks: Track[] }>(
        `/api/heroes/qultureup/worlds/${id}${journeyParam(journeyId)}`
      );
      return response;
    },
    enabled: !!id && isSignedIn && !!token,
  });
}

/**
 * Get tracks for a world
 */
export function useTracks(worldId: string, journeyId?: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();

  return useQuery({
    queryKey: qultureUpKeys.tracks(worldId, journeyId),
    queryFn: async () => {
      const response = await client.get<Track[]>(
        `/api/heroes/qultureup/worlds/${worldId}/tracks${journeyParam(journeyId)}`
      );
      return response;
    },
    enabled: !!worldId && isSignedIn && !!token,
  });
}

/**
 * Get missions for a track
 */
export function useMissions(worldId: string, trackId: string, journeyId?: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();

  return useQuery({
    queryKey: qultureUpKeys.missions(worldId, trackId, journeyId),
    queryFn: async () => {
      const response = await client.get<Mission[]>(
        `/api/heroes/qultureup/worlds/${worldId}/tracks/${trackId}/missions${journeyParam(journeyId)}`
      );
      return response;
    },
    enabled: !!worldId && !!trackId && isSignedIn && !!token,
  });
}

/**
 * Get mission by ID with quiz questions (answers redacted)
 */
export function useMission(id: string, journeyId?: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();

  return useQuery({
    queryKey: qultureUpKeys.mission(id, journeyId),
    queryFn: async () => {
      const response = await client.get<Mission>(
        `/api/heroes/qultureup/missions/${id}${journeyParam(journeyId)}`
      );
      return response;
    },
    enabled: !!id && isSignedIn && !!token,
  });
}

/**
 * Get user progress
 */
export function useQultureUpProgress(journeyId?: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();

  return useQuery({
    queryKey: qultureUpKeys.progress(journeyId),
    queryFn: async () => {
      const response = await client.get<QultureUpProgress>(
        `/api/heroes/qultureup/me/progress${journeyParam(journeyId)}`
      );
      return response;
    },
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get active assessment result
 */
export function useQultureUpAssessment(journeyId?: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();

  return useQuery({
    queryKey: qultureUpKeys.assessment(journeyId),
    queryFn: async () => {
      const response = await client.get<QultureUpAssessment | null>(
        `/api/heroes/qultureup/me/assessment${journeyParam(journeyId)}`
      );
      return response;
    },
    enabled: isSignedIn && !!token,
    staleTime: 0,
  });
}

/**
 * Start a mission.
 * Accepts either a missionId string (legacy) or an object with missionId, worldId, trackId, journeyId.
 */
export function useStartMission(journeyId?: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (arg: string | { missionId: string; worldId?: string; trackId?: string }) => {
      const missionId = typeof arg === 'string' ? arg : arg.missionId;
      const worldId = typeof arg === 'string' ? undefined : arg.worldId;
      const trackId = typeof arg === 'string' ? undefined : arg.trackId;
      const response = await client.post<{ missionId: string; status: string; startedAt: string }>(
        `/api/heroes/qultureup/missions/${missionId}/start${journeyParam(journeyId)}`,
        { worldId, trackId }
      );
      return response;
    },
    onSuccess: (_, arg) => {
      const missionId = typeof arg === 'string' ? arg : arg.missionId;
      queryClient.invalidateQueries({ queryKey: qultureUpKeys.mission(missionId, journeyId) });
      queryClient.invalidateQueries({ queryKey: qultureUpKeys.all });
      queryClient.invalidateQueries({ queryKey: heroesKeys.miles() });
    },
  });
}

/**
 * Complete a mission (non-quiz)
 */
export function useCompleteMission(journeyId?: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      missionId,
      worldId,
      trackId,
      score,
      milesEarned,
    }: {
      missionId: string;
      worldId: string;
      trackId: string;
      score?: number;
      milesEarned?: number;
    }) => {
      const response = await client.post<{
        missionId: string;
        status: string;
        completedAt: string;
        milesEarned: number;
      }>(`/api/heroes/qultureup/missions/${missionId}/complete${journeyParam(journeyId)}`, {
        worldId,
        trackId,
        score,
        milesEarned,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qultureUpKeys.all });
      queryClient.invalidateQueries({ queryKey: heroesKeys.miles() });
    },
  });
}

/**
 * Submit quiz answers for a mission
 */
export function useSubmitQuiz(journeyId?: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      missionId,
      worldId,
      trackId,
      answers,
    }: {
      missionId: string;
      worldId?: string;
      trackId?: string;
      answers: Array<
        | { questionId: number; answerId?: number; answerText?: string }
        | { questionId: string; answer: string }
      >;
    }) => {
      const response = await client.post<QuizEvaluationResult>(
        `/api/heroes/qultureup/missions/${missionId}/quiz${journeyParam(journeyId)}`,
        { worldId, trackId, answers }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qultureUpKeys.all });
      queryClient.invalidateQueries({ queryKey: heroesKeys.miles() });
    },
  });
}

/**
 * Start a knowledge-assessment attempt: the server picks and records the
 * questions (one world, or `all`) and returns them with the attempt id.
 */
export function useStartAssessmentAttempt(journeyId?: string) {
  const { client } = useSdkContext();

  return useMutation({
    mutationFn: async (data: { worldId: string; questionsPerWorld?: number }) =>
      client.post<AssessmentAttempt>(
        `/api/heroes/qultureup/assessment/attempts${journeyParam(journeyId)}`,
        data
      ),
  });
}

/**
 * Answer one question of an attempt. The server grades and records it — the
 * first answer stands — and returns the verdict for formative feedback.
 */
export function useAnswerAssessmentQuestion(journeyId?: string) {
  const { client } = useSdkContext();

  return useMutation({
    mutationFn: async ({
      attemptId,
      ...answer
    }: {
      attemptId: string;
      questionId: number;
      answerId?: number;
      answerText?: string;
    }) =>
      client.post<AssessmentEvaluationResult>(
        `/api/heroes/qultureup/assessment/attempts/${attemptId}/answers${journeyParam(journeyId)}`,
        answer
      ),
  });
}

/**
 * The placement an attempt gives, as the server graded it — what a results
 * screen draws. Enabled once there is an attempt to read.
 */
export function useAssessmentPlacement(attemptId: string | null, journeyId?: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();

  return useQuery({
    queryKey: qultureUpKeys.assessmentPlacement(attemptId ?? '', journeyId),
    queryFn: async () =>
      client.get<AssessmentPlacement>(
        appendJourneyParam(
          `/api/heroes/qultureup/assessment/attempts/${attemptId}/placement`,
          journeyId
        )
      ),
    enabled: !!attemptId && isSignedIn && !!token,
  });
}

/**
 * Save the placement of a knowledge assessment from its attempts.
 */
export function useSaveAssessment(journeyId?: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();

  return useMutation({
    // The attempts the server served — it places them from what it graded; the
    // saved assessment it returns carries the placement to show.
    mutationFn: async (data: {
      attemptIds: string[];
      assessmentData?: Record<string, unknown>;
    }) => {
      const response = await client.post<QultureUpAssessment>(
        `/api/heroes/qultureup/me/assessment${journeyParam(journeyId)}`,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qultureUpKeys.assessment(journeyId) });
      queryClient.invalidateQueries({ queryKey: qultureUpKeys.worlds(journeyId) });
      // The Languages Academy opens after the assessment: its state changes too.
      queryClient.invalidateQueries({ queryKey: languageAcademyKeys.all });
    },
  });
}
